import {type RefObject, useCallback, useLayoutEffect, useMemo, useState} from 'react';
import {useResizeObserver} from './useResizeObserver';

export interface ImageRenderLayout {
  frameWidth: number;
  frameHeight: number;
  imageScale: number;
  imageOffsetY: number;
}

const EMPTY_LAYOUT: ImageRenderLayout = {
  frameWidth: 0,
  frameHeight: 0,
  imageScale: 0,
  imageOffsetY: 0,
};

const areEqual = (left: ImageRenderLayout, right: ImageRenderLayout) =>
  left.frameWidth === right.frameWidth &&
  left.frameHeight === right.frameHeight &&
  left.imageScale === right.imageScale &&
  left.imageOffsetY === right.imageOffsetY;

export const useImageRenderLayout = (
  frameRef: RefObject<HTMLElement | null>,
  imageRef: RefObject<HTMLImageElement | null>,
) => {
  const [layout, setLayout] = useState(EMPTY_LAYOUT);
  const observedElements = useMemo(() => [frameRef, imageRef], [frameRef, imageRef]);

  const syncLayout = useCallback(() => {
    const frame = frameRef.current;
    const image = imageRef.current;
    if (!frame) return;

    const next: ImageRenderLayout = {
      frameWidth: frame.clientWidth,
      frameHeight: frame.clientHeight,
      imageScale: 0,
      imageOffsetY: 0,
    };

    if (image?.naturalWidth && image.clientWidth && image.clientHeight) {
      next.imageScale = image.clientWidth / image.naturalWidth;
      next.imageOffsetY = image.clientHeight - image.naturalHeight * next.imageScale;
    }

    setLayout((current) => (areEqual(current, next) ? current : next));
  }, [frameRef, imageRef]);

  useLayoutEffect(syncLayout, [syncLayout]);
  useResizeObserver(observedElements, syncLayout);

  return {layout, syncLayout};
};
